import { saveAs } from 'file-saver';

import { sanitizeFilename } from '@/utils/filename-utils';

const COPIED_STYLE_PROPS = [
  'fill',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'fill-opacity',
  'opacity',
  'font-size',
  'font-family',
  'font-weight',
  'paint-order',
  'stroke-linecap',
  'stroke-linejoin',
];

const EXPORT_SCALE = 2;
const SVG_NS = 'http://www.w3.org/2000/svg';

// CSS custom properties don't survive SVG serialization, so every var(--…)
// paint value must be resolved to a concrete color before export.
const resolveCssColor = (value: string): string => {
  const probe = document.createElement('div');
  probe.style.color = value;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return resolved;
};

const inlineComputedStyles = (source: SVGSVGElement, clone: SVGSVGElement) => {
  const sourceElements = [source, ...source.querySelectorAll('*')];
  const cloneElements = [clone, ...clone.querySelectorAll('*')];
  sourceElements.forEach((element, index) => {
    const computed = getComputedStyle(element);
    const target = cloneElements[index] as SVGElement;
    COPIED_STYLE_PROPS.forEach((prop) => {
      const value = computed.getPropertyValue(prop);
      if (value) {
        target.style.setProperty(prop, value);
      }
      // Drop var(--…) presentation attributes; the resolved inline style
      // above is the source of truth and vars are meaningless standalone.
      if (target.getAttribute(prop)?.includes('var(')) {
        target.removeAttribute(prop);
      }
    });
  });
};

const buildExportSvg = (svg: SVGSVGElement): SVGSVGElement => {
  const rect = svg.getBoundingClientRect();
  const clone = svg.cloneNode(true) as SVGSVGElement;
  inlineComputedStyles(svg, clone);
  clone.setAttribute('xmlns', SVG_NS);
  clone.setAttribute('width', String(rect.width));
  clone.setAttribute('height', String(rect.height));

  const backgrounds = [resolveCssColor('var(--background)')];
  const containerBackground = svg.parentElement
    ? getComputedStyle(svg.parentElement).backgroundColor
    : '';
  if (containerBackground && containerBackground !== 'rgba(0, 0, 0, 0)') {
    backgrounds.push(containerBackground);
  }
  backgrounds.reverse().forEach((color) => {
    const bg = document.createElementNS(SVG_NS, 'rect');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', color);
    clone.insertBefore(bg, clone.firstChild);
  });
  return clone;
};

const svgToImage = (svg: SVGSVGElement): Promise<HTMLImageElement> => {
  const markup = new XMLSerializer().serializeToString(svg);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to rasterize graph SVG'));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  });
};

const renderPngBlob = async (svg: SVGSVGElement): Promise<Blob> => {
  const rect = svg.getBoundingClientRect();
  const image = await svgToImage(buildExportSvg(svg));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(rect.width * EXPORT_SCALE);
  canvas.height = Math.round(rect.height * EXPORT_SCALE);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }
  ctx.scale(EXPORT_SCALE, EXPORT_SCALE);
  ctx.drawImage(image, 0, 0, rect.width, rect.height);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/png'),
  );
  if (!blob) {
    throw new Error('Failed to encode graph PNG');
  }
  return blob;
};

const safeFileName = (name: string): string =>
  sanitizeFilename(name) || 'context-graph';

export const exportGraphPng = async (
  svg: SVGSVGElement,
  fileName: string,
): Promise<void> => {
  const blob = await renderPngBlob(svg);
  saveAs(blob, `${safeFileName(fileName)}.png`);
};

export const exportGraphSvg = (svg: SVGSVGElement, fileName: string): void => {
  const markup = new XMLSerializer().serializeToString(buildExportSvg(svg));
  const blob = new Blob([markup], { type: 'image/svg+xml;charset=utf-8' });
  saveAs(blob, `${safeFileName(fileName)}.svg`);
};

// ClipboardItem is constructed with the pending promise: Safari only allows
// clipboard.write within transient user activation, which an awaited
// rasterization would outlive.
export const copyGraphPng = (svg: SVGSVGElement): Promise<void> =>
  navigator.clipboard.write([
    new ClipboardItem({ 'image/png': renderPngBlob(svg) }),
  ]);
